import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Tooltip, tooltipClasses } from "@mui/material";
import { toast } from "react-toastify";

import { toast_position } from "../../../../redux/features/Settings/Authorization";

import { getCreateTransactionBalance } from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";

import { formatAccountNumber } from "../../FormatAccountNumber";
import TEXT from "../../../../utils/globalText";
import ICONS from "../../../../assets/Icons";
import IfscCodeMapper from "../../IfscLogoMapper";
import SVGComponent from "../../SVGComponent";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import IconTearDropButton from "../../CommonButtons/IconTearDropButton";
import AnimationUpAndDown from "../../../BeneTransaction/OwnerPopup/AnimationUpAndDown";
import FormatAmount from "../../FormatAmount/FormatAmount";
import UniversalRefreshToast from "../../../_utils/UniversalRefreshToast";
import Loader from "../../../Loader/Index";
import formatTotalBalance from "../../../../utils/formatTotalBalance";
import CommonBankLogo from "../../CommonBankLogo";
import TickerAnimation from "../../TickerAnimation";
import accountNameStandardised from "../../../../utils/accountNamestandardised";
import CommonAccountModal from "../SourceDestinationAccountDetail/CommonAccountModal";
import CommonToast from "../../CommonToast";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";

// Suggestions of props for component while using the components -------------------------------------
/**
 * @param {String} props.type - passs type like "source" or "destination"
 * @param {String} props.flow - If type is "destination" or "internal", then pass "beneficiary" or "internal"
 * @param {String} props.page - If you want this component on confirm page then, pass "confirm"
 * @param {Boolean} props.modalOpen - Is modal open or not
 * @param {Object} props.setModalOpen - Setter of modal state ( true or false )
 * @param {Object} props.txnPayLoad - txnPayload array
 * @param {Object} props.setTxnPayLoad - Setter of txnPayload
 * @param {Boolean} props.shouldShowAssociatedAccounts -  If you are using type = "destination", then pass state of showing associted accounts found
 * @param {Function} props.handleOnAccountSelection - If you are using type = "destination", then pass handle function of Associate account found
 * @returns {JSX.Element}
 */

const SourceDestinationAccountDetailVariant = ({
  type,
  flow,
  page,
  modalOpen,
  setModalOpen,
  txnPayLoad,
  setTxnPayLoad,
  shouldShowAssociatedAccounts,
  handleOnAccountSelection,
  isDisabled,
  isEditDisabled,
  isReimbursement,
  activeType,
  setActiveType,
  setRemPan,
  setRemAccount,
  setRemIFSC,
  remIFSC,
  remAccount,
  remPan,
  TypeRTGS,
  currentAccountDetails,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const services = useSelector((state) => state.refreshToken.services);
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const requestActiveRef = useRef(false);
  const toastIdRef = useRef(null);
  const completedRef = useRef(false);
  const hasSeenLoadingRef = useRef(false);

  //states, variables and logics for Source account (starts) ---------------------------------
  const [balanceRefresh, setBalanceRefresh] = useState(false);
  const [refreshAnimationId, setRefreshAnimationId] = useState(null);
  const [previousRefreshData, setPreviousRefreshData] = useState(null);
  const [balanceRefreshDisabled, setBalanceRefreshDisabled] = useState(false);
  const [refreshType, setRefreshType] = useState(false);
  const [transactionFilterData, setTransactionFilterData] = useState([]);
  const {
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshError,
    createTransactionBankRefreshData,
  } = useSelector((state) => state.ownerAllVerification);

  const { beneOwnerData } =
    useSelector((state) => state.beneTransactionAccounts) || {};

  useEffect(() => {
    setTransactionFilterData(beneOwnerData?.owner_accounts);
  }, [beneOwnerData]);

  const cleanupAfterRefresh = () => {
    toastIdRef.current = null;
    requestActiveRef.current = false;
    completedRef.current = false;
    hasSeenLoadingRef.current = false;

    setBalanceRefreshDisabled(false);
  };

  useEffect(() => {
    // Track when loading starts to ensure we only process errors from actual API calls
    if (createTransactionBankRefreshLoading && requestActiveRef.current) {
      hasSeenLoadingRef.current = true;
      if (!toastIdRef.current) {
        UniversalRefreshToast();
      }
      return;
    }

    // Ignore anything after completion or if no request was made
    if (!requestActiveRef.current || completedRef.current) return;

    // Only process success/error if we've seen loading start (to avoid stale errors)
    if (!hasSeenLoadingRef.current) return;

    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshData?.status
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);
      CommonToast({
        text: "Balance refreshed successfully.",
        icon: "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg",
        variant: "var(--success)",
      });

      cleanupAfterRefresh();
      return;
    }

    // Only show error if a request was actually made and we've seen loading
    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshError &&
      requestActiveRef.current &&
      hasSeenLoadingRef.current
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);
      CommonToast({
        text: createTransactionBankRefreshError,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
      });

      cleanupAfterRefresh();
    }
  }, [
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshData,
    createTransactionBankRefreshError,
  ]);

  const handleRefresh = (e, id, prevBalance) => {
    e.stopPropagation();

    if (
      !(services?.includes("VAccountBalance") || services?.includes("Admin"))
    ) {
      navigate("/feature_not_assigned");
      return;
    }

    if (requestActiveRef.current) return;

    requestActiveRef.current = true;
    completedRef.current = false;
    toastIdRef.current = null;

    setBalanceRefreshDisabled(true);

    setPreviousRefreshData(prevBalance);
    setRefreshType("single");
    setRefreshAnimationId(id);

    dispatch(getCreateTransactionBalance(id));
  };

  //states, variables and logics  for Source account (Ends) ---------------------------------

  const handleOpenModal = () => {
    if (!isEditDisabled) {
      if (page !== "confirm") {
        setModalOpen(true);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleAssociateOpenModal = () => {
    setRemPan(txnPayLoad?.pan);
    setAssociateModalOpen(true);
  };

  const handleAssociateCloseModal = () => {
    setAssociateModalOpen(false);
  };

  const registerNewAccount = () => {
    navigate("/beneficiary_account_dashboard/create_beneficiary_account");
  };

  const hasBUAccountService = services?.includes("BUAccount");

  return (
    <>
      {modalOpen && (type === "destination" || flow === "internal") && (
        <div className="fixed top-top-base left-left-base w-full-width min-h-full-height flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <CommonAccountModal
            onClose={handleCloseModal}
            sourceDestiAcc={flow === "beneficiary" ? true : false}
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            flow={flow === "beneficiary" ? "beneTxn" : "internalTxn"}
            activeType={activeType}
            setActiveType={setActiveType}
            setRemPan={setRemPan}
            setRemAccount={setRemAccount}
            setRemIFSC={setRemIFSC}
            remIFSC={remIFSC}
            remAccount={remAccount}
            remPan={remPan}
            TypeRTGS={TypeRTGS}
            currentAccountDetails={currentAccountDetails}
          />
        </div>
      )}

      {modalOpen && type === "source" && (
        <div className="fixed top-top-base left-left-base w-full-width min-h-full-height flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <CommonAccountModal
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            type={"source"}
            setOpenOwner={setModalOpen}
            TypeRTGS={TypeRTGS}
            currentAccountDetails={currentAccountDetails}
          />
        </div>
      )}
      {associateModalOpen && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <CommonAccountModal
            sourceDestiAcc={false}
            onClose={handleAssociateCloseModal}
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            panNumber={txnPayLoad?.pan}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            BeneTxnAssocAcct={true}
            setAssociateModalOpen={setAssociateModalOpen}
            flow={flow === "internal" ? "" : "beneTxn"}
            accountType={flow === "internal" ?? "internal"}
            associateTxn={flow === "internal" ?? "associateTxn"}
            ispanbankRequired={true}
            activeType={activeType}
            setActiveType={setActiveType}
            setRemPan={setRemPan}
            setRemAccount={setRemAccount}
            setRemIFSC={setRemIFSC}
            remIFSC={remIFSC}
            remAccount={remAccount}
            remPan={remPan}
            associateModalOpen={associateModalOpen}
          />
        </div>
      )}

      <div className="w-width-full">
        <div
          className={`${isDisabled ? "" : "bg-emphasis"}  flex flex-col relative rounded-border-radius-md`}
        >
          <div
            className={`bg-primary-nav flex flex-col relative rounded-border-radius-md ${isDisabled ? "opacity-light-opacity cursor-not-allowed pointer-events-none" : ""}`}
          >
            <div
              className={`p-padding-md h-height-full bg-primary-card flex flex-col rounded-border-radius-md`}
            >
              {page !== "confirm" &&
              ((txnPayLoad?.bankAccountNo &&
                txnPayLoad?.bankAccountIFSC &&
                type !== "source") ||
                (txnPayLoad?.ownerBankAccountNo &&
                  txnPayLoad?.ownerBankIFSC &&
                  type === "source")) ? (
                <div className="top-top-base right-right-base absolute">
                  <IconTearDropButton
                    handleFunction={handleOpenModal}
                    isDisabled={isEditDisabled || false}
                    isLoading={false}
                    enabledBgColor={"var(--primary-nav-act)"}
                    icon="https://q2pay.in/new_assets/common/create_flow/edit.svg"
                    hoverOption="white"
                    title={TEXT?.EDIT}
                  />
                </div>
              ) : null}
              <div
                className={`flex flex-col gap-gap-md ${(txnPayLoad?.bankAccountNo && txnPayLoad?.bankAccountIFSC && type !== "source") || (txnPayLoad?.ownerBankAccountNo && txnPayLoad?.ownerBankIFSC && type === "source") ? " mb-margin-base" : ""}`}
              >
                <div className="flex flex-col gap-gap-xs">
                  <span className="text-font-md text-text-primary font-semibold tracking-letter-space">
                    {type === "source"
                      ? TEXT?.DEFAULT_SOURCE_ACCOUNT_CAPITALIZE
                      : TEXT?.DESTINATION_ACCOUNT_CAPITALIZE}
                  </span>
                  <span className="text-font-xs text-text-secondary">
                    {type === "source"
                      ? TEXT?.SOURCE_ACCOUNT_DES_TEXT
                      : TEXT?.DESTINATION_ACCOUNT_DES_TEXT}
                  </span>
                </div>

                {(!txnPayLoad?.bankAccountNo &&
                  !txnPayLoad?.bankAccountIFSC &&
                  type !== "source") ||
                (!txnPayLoad?.ownerBankAccountNo &&
                  !txnPayLoad?.ownerBankIFSC &&
                  type === "source") ? (
                  <div>
                    <PrimaryWithIconButton
                      btnText={
                        isReimbursement
                          ? TEXT?.SELECT_REIMBURSEMENT_ACCOUNT
                          : type === "source"
                            ? TEXT?.SELECT_OWNER_ACCOUNT
                            : type === "destination" && flow === "internal"
                              ? TEXT?.SELECT_DESTINATION_ACCOUNT
                              : TEXT?.SELECT_BENEFICIARY_ACCOUNT
                      }
                      enabledBgColor="var(--primary-nav-act)"
                      isDisabled={false}
                      handleFunction={handleOpenModal}
                      iconToBeAlign="left"
                      enabledTextColor={"var(--text-primary)"}
                      icon={ICONS?.SELECT_ACCOUNT}
                      fullWidthRequired={true}
                      hoverOption="white"
                    />
                    {type === "destination" &&
                      flow === "beneficiary" &&
                      !txnPayLoad?.bankAccountNo &&
                      !txnPayLoad?.bankAccountIFSC && (
                        <div className="mt-margin-md">
                          <PrimaryWithIconButton
                            btnText={TEXT?.REGISTER_NEW_ACCOUNT}
                            enabledBgColor="var(--primary-nav-act)"
                            handleFunction={registerNewAccount}
                            isDisabled={!hasBUAccountService}
                            iconToBeAlign="left"
                            enabledTextColor="var(--text-primary)"
                            icon={
                              "https://q2pay.in/new_assets/common/settings/add_button_white.svg"
                            }
                            imgColor="var(--text-primary)"
                            fullWidthRequired
                            hoverOption="white"
                          />
                        </div>
                      )}
                  </div>
                ) : (txnPayLoad?.bankAccountNo &&
                    txnPayLoad?.bankAccountIFSC &&
                    type !== "source") ||
                  (txnPayLoad?.ownerBankAccountNo &&
                    txnPayLoad?.ownerBankIFSC &&
                    type === "source") ? (
                  <>
                    <div className="flex flex-col gap-gap-md">
                      <div className="flex flex-col w-width-full h-auto py-padding-xs px-padding-sm bg-primary-nav rounded-border-radius-sm gap-gap-xs">
                        <span className="text-font-sm text-text-primary font-semibold tracking-letter-space">
                          {type === "source"
                            ? formatAccountNumber(
                                txnPayLoad?.ownerBankAccountNo,
                              )
                            : formatAccountNumber(txnPayLoad?.bankAccountNo)}
                        </span>
                        <span className="text-font-xs text-text-secondary">
                          {" "}
                          {TEXT?.ACCOUNT_NUMBER}
                        </span>
                      </div>
                      {type !== "source" ? (
                        <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs">
                          <div className="text-font-sm text-text-primary font-semibold tracking-letter-space">
                            <div className="flex-1 truncate flex">
                              <TickerAnimation
                                text={accountNameStandardised(
                                  txnPayLoad?.bankAccountName,
                                )}
                              />
                            </div>
                          </div>
                          <span className="text-font-xs text-text-secondary">
                            {" "}
                            {TEXT?.ACCOUNT_NAME}
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-col relative Qlg:flex-row w-width-full h-auto py-padding-xs px-padding-sm bg-primary-nav rounded-border-radius-sm Qlg:gap-gap-xs gap-gap-base justify-between">
                            <div className="flex flex-1 flex-col gap-gap-xs">
                              <span className="text-font-sm text-text-primary font-semibold tracking-letter-space">
                                {txnPayLoad?.ownerBankIFSC}
                              </span>
                              <span className="text-font-xs text-text-secondary">
                                {TEXT?.IFSC_CODE}
                              </span>
                            </div>
                            <div className="flex items-center justify-between absolute right-right-base bottom-bottom-base">
                              <div className="bg-text-primary rounded-tl-border-radius-md rounded-br-border-radius-sm flex items-center justify-around w-width-full h-full py-padding-sm px-padding-md">
                                <CommonBankLogo
                                  ifsc={
                                    type === "destination"
                                      ? txnPayLoad?.bankAccountIFSC
                                      : txnPayLoad?.ownerBankIFSC
                                  }
                                  variant="small"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col Qlg:flex-row w-width-full h-auto py-padding-xs px-padding-sm bg-primary-nav rounded-border-radius-sm Qlg:gap-gap-xs gap-gap-base justify-between">
                            <div className="w-width-full flex items-center justify-between">
                              <AnimationUpAndDown
                                loader={createTransactionBankRefreshLoading}
                                currentId={txnPayLoad?.ownerId}
                                previousId={refreshAnimationId}
                                currentBalance={txnPayLoad?.ownerBankBalance}
                                previousBalance={previousRefreshData}
                                refreshType={refreshType}
                                classType={"beneOwner"}
                              />
                              <div className="flex flex-col gap-gap-xs">
                                <div className="flex items-center gap-gap-sm relative">
                                  <div
                                    className={`text-left text-font-sm tracking-letter-space 
                                 ${
                                   txnPayLoad?.ownerBankBalance
                                     ? txnPayLoad?.ownerBankBalance <
                                         GlobalConfiguration?.lengthCheckZero &&
                                       !(
                                         isNaN(txnPayLoad?.ownerBankBalance) ||
                                         txnPayLoad?.ownerBankBalance ===
                                           "xxxx.xx" ||
                                         txnPayLoad?.ownerBankBalance ===
                                           null ||
                                         txnPayLoad?.ownerBankBalance ===
                                           undefined
                                       )
                                       ? "text-failed font-semibold"
                                       : "text-settled font-semibold"
                                     : "text-text-primary font-normal"
                                 }`}
                                  >
                                    INR{" "}
                                    {(txnPayLoad?.ownerBankBalance ??
                                    formatTotalBalance(
                                      txnPayLoad?.ownerBankBalance,
                                      "Default",
                                    ).prefix === "crores")
                                      ? formatTotalBalance(
                                          txnPayLoad?.ownerBankBalance,
                                          "crores",
                                        ).amount
                                      : formatTotalBalance(
                                          txnPayLoad?.ownerBankBalance,
                                          "Default",
                                        ).amount}
                                  </div>
                                </div>
                                <div className="text-left text-font-xs text-text-secondary whitespace-nowrap">
                                  <div className="w-full max-w-full overflow-hidden">
                                    <TickerAnimation
                                      text={`${
                                        txnPayLoad?.ownerBankBalance
                                          ? formatTotalBalance(
                                              txnPayLoad?.ownerBankBalance,
                                              "Default",
                                            ).prefix === "crores"
                                            ? `Balance in ${
                                                formatTotalBalance(
                                                  txnPayLoad?.ownerBankBalance,
                                                  "Default",
                                                ).prefix
                                              } - `
                                            : TEXT.BALANCE_UPDATED
                                          : TEXT.BALANCE_UPDATED
                                      } ${txnPayLoad?.ownerLastUpdated}`}
                                    />
                                  </div>
                                </div>
                              </div>
                              <Tooltip
                                componentsProps={{
                                  popper: {
                                    sx: {
                                      [`& .${tooltipClasses.tooltip}`]: {
                                        backgroundColor: (theme) =>
                                          theme.palette.common.black,
                                      },
                                    },
                                  },
                                }}
                                title="Refresh Balance"
                              >
                                <button
                                  disabled={balanceRefreshDisabled}
                                  className={`w-icon-md h-icon-md flex flex-shrink-0 bg-primary-card rounded-border-radius-md justify-center items-center ${services?.includes("VAccountBalance") || services?.includes("Admin") ? "cursor-pointer" : "cursor-not-allowed opacity-light-opacity"}`}
                                  onClick={(e) =>
                                    handleRefresh(
                                      e,
                                      txnPayLoad?.ownerId,
                                      txnPayLoad?.ownerBankBalance,
                                    )
                                  }
                                >
                                  {createTransactionBankRefreshLoading ||
                                  balanceRefresh ? (
                                    <Loader
                                      height="var(--icon-sm)"
                                      width="var(--icon-sm)"
                                    />
                                  ) : (
                                    <img
                                      src={ICONS?.RELOAD_REFRESH_WHITE}
                                      alt="reload"
                                      className="w-icon-sm h-icon-sm"
                                    />
                                  )}
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* <div className="flex items-center justify-between absolute right-right_base bottom-bottom_base">
                      <div className="bg-text_primary rounded-tl-border_radius_md rounded-br-border_radius_md flex items-center justify-around w-width-full h-full py-padding_sm px-padding_md">
                        <CommonBankLogo
                          ifsc={
                            type === "destination"
                              ? txnPayLoad?.bankAccountIFSC
                              : txnPayLoad?.ownerBankIFSC
                          }
                          variant="big"
                        />
                      </div>
                    </div> */}
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="w-width-full flex flex-col gap-gap-md">
              {shouldShowAssociatedAccounts &&
                type === "destination" &&
                (flow === "beneficiary" || flow === "internal") && (
                  <div className="py-padding-sm px-padding-sm">
                    <div className="rounded-border-radius-md flex w-width-full px-padding-sm py-padding-sm global-hover-black z-z-index-content relative hover:rounded-border-radius-md">
                      <div
                        className="w-width-full flex items-center text-text-primary cursor-pointer"
                        onClick={handleAssociateOpenModal}
                      >
                        <div className="flex items-start w-width-full">
                          <div className="pt-padding-xs h-icon-md w-icon-md">
                            <img
                              src={ICONS?.INFO_FILLED}
                              className="h-icon-md w-icon-md"
                            />
                          </div>
                          <div className="flex flex-col gap-gap-xs ml-margin-sm">
                            <div className="text-text-primary text-font-md">
                              {TEXT?.ACTIVE_ASSOCIATED_ACCOUNTS}
                            </div>
                            <div className="text-text-secondary text-font-xs">
                              {flow === "beneficiary"
                                ? TEXT?.OTHER_EXISTING_BENEFICIARY_ACCOUNTS_FOUND
                                : TEXT?.OTHER_EXISTING_INTERNAL_ACCOUNTS_FOUND}
                            </div>
                          </div>
                        </div>

                        <div className="h-height-full pt-padding-xs w-icon-md cursor-pointer flex-shrink-0">
                          <SVGComponent
                            url={
                              "https://q2pay.in/new_assets/common/arrows/arrow_right_white.svg"
                            }
                            color={"var(--text-primary)"}
                            height={"var(--icon-md)"}
                            width={"var(--icon-md)"}
                            alt="Arrow"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SourceDestinationAccountDetailVariant;
